import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CheckCircle2, ChevronDown, ClipboardCheck, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const CHECKLIST_ITEMS = [
  { id: "identity_verified", label: "Patient identity verified" },
  { id: "history_reviewed", label: "Medical history reviewed" },
  { id: "medications_reviewed", label: "Current medications reviewed" },
  { id: "allergies_confirmed", label: "Allergies confirmed" },
  { id: "treatment_discussed", label: "Treatment plan discussed with patient" },
  { id: "risks_explained", label: "Risks and benefits explained" },
  { id: "consent_obtained", label: "Patient consent obtained" },
  { id: "notes_documented", label: "Clinical notes documented" },
  { id: "followup_established", label: "Follow-up plan established" },
] as const;

interface SessionFinalizationChecklistProps {
  assessmentId: string;
  providerId: string;
}

const SessionFinalizationChecklist = ({ assessmentId, providerId }: SessionFinalizationChecklistProps) => {
  const { toast } = useToast();
  const [items, setItems] = useState<Record<string, boolean>>({});
  const [finalizedAt, setFinalizedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("session_checklist" as any)
        .select("items_completed, finalized_at")
        .eq("assessment_id", assessmentId)
        .eq("provider_id", providerId)
        .maybeSingle();
      if (data) {
        setItems((data as any).items_completed || {});
        setFinalizedAt((data as any).finalized_at || null);
      }
      setLoading(false);
    };
    load();
  }, [assessmentId, providerId]);

  const completedCount = Object.values(items).filter(Boolean).length;
  const allComplete = completedCount === CHECKLIST_ITEMS.length;

  const toggleItem = async (itemId: string) => {
    if (finalizedAt) return;
    const updated = { ...items, [itemId]: !items[itemId] };
    setItems(updated);

    // Upsert to DB
    await supabase.from("session_checklist" as any).upsert({
      assessment_id: assessmentId,
      provider_id: providerId,
      items_completed: updated,
    }, { onConflict: "assessment_id,provider_id" });
  };

  const handleFinalize = async () => {
    if (!allComplete) return;
    setSaving(true);
    const { error } = await supabase
      .from("session_checklist" as any)
      .upsert({
        assessment_id: assessmentId,
        provider_id: providerId,
        items_completed: items,
        finalized_at: new Date().toISOString(),
      }, { onConflict: "assessment_id,provider_id" });

    if (error) {
      toast({ title: "Error", description: "Failed to finalize session.", variant: "destructive" });
    } else {
      setFinalizedAt(new Date().toISOString());
      toast({ title: "Session Finalized", description: "This session is now eligible for payout." });
    }
    setSaving(false);
  };

  if (loading) return null;

  if (finalizedAt) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/5 border border-primary/20">
        <CheckCircle2 size={20} className="text-primary shrink-0" />
        <div>
          <p className="text-sm font-semibold text-foreground">Session Finalized</p>
          <p className="text-xs text-muted-foreground">
            Completed on {new Date(finalizedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </p>
        </div>
        <Badge variant="secondary" className="ml-auto text-xs gap-1"><Lock size={10} /> Locked</Badge>
      </div>
    );
  }

  return (
    <Collapsible>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors cursor-pointer">
          <div className="flex items-center gap-2">
            <ClipboardCheck size={16} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">Session Finalization</span>
            <Badge variant="outline" className="text-xs">{completedCount}/{CHECKLIST_ITEMS.length}</Badge>
          </div>
          <ChevronDown size={14} className="text-muted-foreground" />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Card className="mt-2 border-border">
          <CardContent className="p-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              Complete all items before finalizing. Finalized sessions are eligible for payout.
            </p>
            {CHECKLIST_ITEMS.map((item) => (
              <div key={item.id} className="flex items-center gap-3">
                <Checkbox
                  id={`check-${item.id}`}
                  checked={!!items[item.id]}
                  onCheckedChange={() => toggleItem(item.id)}
                />
                <label
                  htmlFor={`check-${item.id}`}
                  className={`text-sm cursor-pointer ${items[item.id] ? "text-muted-foreground line-through" : "text-foreground"}`}
                >
                  {item.label}
                </label>
              </div>
            ))}
            <Button
              onClick={handleFinalize}
              disabled={!allComplete || saving}
              className="w-full mt-2 gap-2"
            >
              <Lock size={14} />
              {saving ? "Finalizing..." : "Finalize Session"}
            </Button>
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default SessionFinalizationChecklist;
